import {Engine} from "platform/engine/Engine";
import {Http, HttpReject} from "platform/network/http/Http";
import Utils from "platform/util/Utils";
import {LangCode} from "platform/enum/LangCode";
import {LanguageUtil} from "platform/util/LanguageUtil";
import Platform from "platform/Platform";
import {
    EduTradingCompleteLessonPayload,
    EduTradingFetchArticlesPayload,
    EduTradingFetchCryptoCalendarEventsPayload,
    EduTradingFetchEconomicCalendarEventsPayload,
    EduTradingFetchMarketVideosPayload,
    EduTradingSetAcademyPlaylistProgress,
    EduTradingSetActivePackage, EduTradingSetActiveTradeContentTab, EduTradingSetActiveTradeMobileTab,
    EduTradingSetArticles,
    EduTradingSetCryptoCalendarEvents,
    EduTradingSetCryptoCalendarFilters,
    EduTradingSetCurrencyStrengthMeter,
    EduTradingSetEconomicCalendarEvents,
    EduTradingSetEconomicCalendarFilters,
    EduTradingSetGlossary,
    EduTradingSetLessonsCount,
    EduTradingSetLoading,
    EduTradingSetLoadingTree,
    EduTradingSetMarketSearchVideos,
    EduTradingSetPackages,
    EduTradingSetPackageTreeContent,
    EduTradingSetTrendAnalisys
} from "core/redux/eduTrading/EduTradingReduxActions";
import {EduTradingArticle, EduTradingArticlesResponse} from "protocol/edutrading/EduTradingArticlesResponse";
import {EduTradingEconomicCalendarEventsResponse} from "protocol/edutrading/EduTradingEconomicCalendarEventsResponse";
import {TSMap} from "typescript-map";
import {EduCalendarFilterType} from "enum/EduCalendarFilterType";
import {EduFilterValue, EduTradingPackage} from "core/redux/eduTrading/EduTradingReduxState";
import DateTimeFormat from "core/format/DateTimeFormat";
import {
    EduTradingCryptoCalendarEvent,
    EduTradingCryptoCalendarEventsResponse
} from "protocol/edutrading/EduTradingCryptoCalendarEventsResponse";
import {
    EduTradingMarketVideo,
    EduTradingMarketVideosResponse
} from "protocol/edutrading/EduTradingMarketVideosResponse";
import {EduTradingCurrencyStrengthMeterResponse} from "protocol/edutrading/EduTradingCurrencyStrengthMeterResponse";
import {EduTradingGlossaryResponse} from "protocol/edutrading/EduTradingGlossaryResponse";
import {EduTradingTrendAnalysisResponse} from "protocol/edutrading/EduTradingTrendAnalysisResponse";
import {XhrUtil} from "core/util/XhrUtil";
import {SetAuthStatusPayload} from "core/redux/auth/AuthReduxActions";
import {StoreState} from "core/redux/StoreState";
import {GetActivePackageResponse} from "protocol/edutrading/GetActivePackageResponse";
import {
    EduTreeCourse,
    EduTreeLesson,
    EduTreeSection,
    GetEducationalContentTree
} from "protocol/edutrading/GetEducationalContentTree";
import {GetLessonContent} from "protocol/edutrading/GetLessonContent";
import {Configuration} from "core/configuration/Configuration";
import {EduTradingLessonsCountType} from "enum/EduTradingLessonsCountType";
import {EduTradingPlaylistUtil} from "core/util/EduTradingPlaylistUtil";
import {SetLangCodePayload} from "platform/redux/translation/TranslationActions";
import {EduTableFilterUtil} from "core/util/EduTableFilterUtil";
import {EduTradingNewsCategoryType} from "protocol/edutrading/EduTradingNewsCategoryType";
import {RouteType} from "core/router/Routes";
import {EduTradingMarketVideoType} from "protocol/edutrading/EduTradingMarketVideoType";
import {GetPurchasePackagesResponse, PackagePurchaseDetails} from "protocol/edutrading/GetPurchasePackagesResponse";
import {EduTradingState} from "core/state/EduTradingState";
import {SetDynamicConfigurationPayload} from "core/redux/app/AppReduxActions";
import {EduTradingTradeContentTab} from "enum/EduTradingTradeContentTab";
import {SetFollowedAccounts, SetFollowedAccountsPayload} from "core/redux/partner/PartnerReduxActions";

const EduSupportedLanguages: string[] = [LangCode.EN, LangCode.IT, LangCode.DE, LangCode.PL, LangCode.SV, LangCode.AR, LangCode.FR, LangCode.ES, LangCode.JA, LangCode.TH, LangCode.PT];
const FilesReplaceDomain = (url: string): string => {
    return url?.replace("https://files.traducationfx.com", "https://files-tfx.tradenetworks.com")
};

export default class EduTradingEngine extends Engine {

    private static _instance: EduTradingEngine;
    private API_URL: string = "https://api-tfx.tradenetworks.com/";
    private API_KEY: string = "thebrainKYZRDSzn2tN";
    private _activePackageInterval: any;

    public static instance(): EduTradingEngine {
        return this._instance || (this._instance = new this());
    }

    public doSetAuthStatus = (payload: SetAuthStatusPayload): void => {
        const {IsEduTrading} = Platform.config<Configuration>();
        if (IsEduTrading) {
            const {connected, authenticated} = Platform.reduxState<StoreState>().auth;
            if (connected) {
                if (!authenticated && payload.authStatus.authenticated && Utils.isNull(this._activePackageInterval)) {
                    this.DoGetEducationalPackages();
                    this.DoGetEducationalContentTree(LanguageUtil.languageCode());
                    this.DoGetActivePackage();
                    this._activePackageInterval = setInterval(this.DoGetActivePackage, 60 * 1000);
                } else if (authenticated && !payload.authStatus.authenticated && Utils.isNotNull(this._activePackageInterval)) {
                    clearInterval(this._activePackageInterval);
                    this._activePackageInterval = null;
                }
            }
        }
    }

    public onSetLanguage = async (payload: SetLangCodePayload) => {
        this.DoGetEducationalContentTree(payload.langCode);
    }

    public onSetDynamicConfiguration = ({configuration}: SetDynamicConfigurationPayload) => {
        if (configuration.AcademyBrokersReviewUrl) {
            Platform.dispatch(EduTradingSetActiveTradeContentTab({
                Tab: EduTradingTradeContentTab.Review
            }));
        }
    }

    public onSetFollowedAccounts = ({Accounts}: SetFollowedAccountsPayload) => {
        const {dynamicConfiguration} = Platform.reduxState<StoreState>().app;
        const activeTab: EduTradingTradeContentTab = Accounts?.size() > 0 ? EduTradingTradeContentTab.Trade
            : dynamicConfiguration.AcademyBrokersReviewUrl ? EduTradingTradeContentTab.Review : EduTradingTradeContentTab.Connect;
        Platform.dispatch(EduTradingSetActiveTradeContentTab({
            Tab: activeTab
        }));
        Platform.dispatch(EduTradingSetActiveTradeMobileTab({
            Tab: Accounts?.size() > 0 ? EduTradingTradeContentTab.Brokers : activeTab
        }));
    }

    private query = async <R>(path: string, params?: {[key: string]: string | number}, language?: LangCode): Promise<R> => {
        let langCode: string = language || LanguageUtil.languageCode();
        langCode = EduSupportedLanguages.indexOf(langCode) > -1 ? langCode : LangCode.EN;
        if (langCode === LangCode.PT) {
            langCode = "pt-BR";
        }
        this._logger.debug(`Start to fetch from path: ${path}. Language: ${langCode}`);
        let url: string = `${this.API_URL}${path}?apiKey=${this.API_KEY}&langCode=${langCode}`;
        if (!Utils.isObjectEmpty(params)) {
            url += "&" + Object.keys(params).map((key: string) => `${key}=${params[key]}`).join("&");
        }
        const answer: [HttpReject, string] = await Utils.to(Http.get(url));
        if (answer[0]) {
            this._logger.warn(`Failed to call: ${url}`);
        } else {
            try {
                this._logger.debug("Parsing response from path: ", path);
                return JSON.parse(answer[1]);
            } catch (e) {
                this._logger.warn(`Failed parse response from: ${url}`);
            }
        }
        return null;
    }

    public DoGetArticles = async ({articleType}: EduTradingFetchArticlesPayload): Promise<void> => {
        const page: RouteType = articleType === EduTradingNewsCategoryType.TechnicalAnalysis ? RouteType.TechnicalAnalysisArticles : RouteType.News;
        Platform.dispatch(EduTradingSetLoading({value: true, page}));
        const response: EduTradingArticlesResponse = await this.query("articles/get-recent-articles", {
            category: articleType,
            limit: 100,
            // market: "forex" | "crypto"
            // offset: 0
        });
        response?.forEach((article: EduTradingArticle) => {
            article.image = FilesReplaceDomain(article.image);
        });
        Platform.dispatch(EduTradingSetLoading({value: false, page}));
        Platform.dispatch(EduTradingSetArticles({
            Articles: Utils.isArrayNotEmpty(response) ? response : [],
            articleType
        }));
    }

    public DoGetEconomicCalendarEvents = async ({Period, dateFilter}: EduTradingFetchEconomicCalendarEventsPayload): Promise<void> => {
        const {economicCalendarFilters} = Platform.reduxState<StoreState>().eduTrading;
        let request;
        if (Utils.isNotNull(dateFilter)) {
            const dateFrom = dateFilter?.from ? DateTimeFormat.dateFormatInput(DateTimeFormat.Of(dateFilter?.from)) : null;
            const dateTo = dateFilter?.from ? DateTimeFormat.dateFormatInput(DateTimeFormat.Of(dateFilter?.to)) : null;
            request = {
                dateFrom,
                dateTo
            }
        } else {
            request = {dateRange: Period}
        }
        Platform.dispatch(EduTradingSetLoading({value: true, page: RouteType.EconomicCalendar}));
        const response: EduTradingEconomicCalendarEventsResponse = await this.query("economic-calendar/get-events", request);

        const Filters: TSMap<EduCalendarFilterType, EduFilterValue[]> = new TSMap();
        const currencies: EduFilterValue[] = [];
        const impact: EduFilterValue[] = [];
        const hasFilters: boolean = economicCalendarFilters.size() > 0;

        response?.forEach(item => {
            if (!currencies?.find(currency => currency.name === item.currencyCode)) {
                const filterValue: EduFilterValue = hasFilters ? economicCalendarFilters.get(EduCalendarFilterType.Currency)?.find(filter => filter.name === item.currencyCode) : null;
                currencies.push({name: item.currencyCode, value: !Utils.isObjectEmpty(filterValue) ? filterValue.value : true});
            }
            if (!impact?.find(impact => impact.name === item.impact)) {
                const filterValue: EduFilterValue = hasFilters ? economicCalendarFilters.get(EduCalendarFilterType.Impact)?.find(filter => filter.name === item.impact) : null;
                impact.push({name: item.impact, value: !Utils.isObjectEmpty(filterValue) ? filterValue.value : true});
            }
        })

        Filters.set(EduCalendarFilterType.Currency, hasFilters ? EduTableFilterUtil.compareFilters(economicCalendarFilters.get(EduCalendarFilterType.Currency), currencies) : currencies);
        Filters.set(EduCalendarFilterType.Impact, hasFilters ? EduTableFilterUtil.compareFilters(economicCalendarFilters.get(EduCalendarFilterType.Impact), impact) : impact);

        Platform.dispatch(EduTradingSetLoading({value: false, page: RouteType.EconomicCalendar}));

        Platform.dispatch(EduTradingSetEconomicCalendarEvents({
            Events: response
        }));

        Platform.dispatch(EduTradingSetEconomicCalendarFilters({
            filters: Filters
        }));
    }

    public DoGetCryptoCalendarEvents = async ({Period, dateFilter}: EduTradingFetchCryptoCalendarEventsPayload): Promise<void> => {
        const {cryptoCalendarFilters} = Platform.reduxState<StoreState>().eduTrading;
        let request;
        if (Utils.isNotNull(dateFilter)) {
            const dateFrom = dateFilter?.from ? DateTimeFormat.dateFormatInput(DateTimeFormat.Of(dateFilter?.from)) : null;
            const dateTo = dateFilter?.from ? DateTimeFormat.dateFormatInput(DateTimeFormat.Of(dateFilter?.to)) : null;
            request = {
                dateFrom,
                dateTo
            }
        } else {
            request = {dateRange: Period}
        }
        Platform.dispatch(EduTradingSetLoading({value: true, page: RouteType.CryptocurrencyCalendar}));
        const response: EduTradingCryptoCalendarEventsResponse = await this.query("crypto-calendar/get-events", request);
        response.forEach((event: EduTradingCryptoCalendarEvent) => {
            event.currencyLogoUrl = FilesReplaceDomain(event.currencyLogoUrl);
        });
        const Filters: TSMap<EduCalendarFilterType, EduFilterValue[]> = new TSMap();
        const impact: EduFilterValue[] = [];
        const hasFilters: boolean = cryptoCalendarFilters.size() > 0;

        response?.forEach(item => {
            if (!impact?.find(impact => impact.name === item.impact) || Utils.isArrayEmpty(impact)) {
                const filterValue: EduFilterValue = hasFilters ? cryptoCalendarFilters.get(EduCalendarFilterType.Impact)?.find(filter => filter.name === item.impact) : null;
                impact.push({name: item.impact, value: !Utils.isObjectEmpty(filterValue) ? filterValue.value : true});
            }
        });

        Filters.set(EduCalendarFilterType.Impact, hasFilters ? EduTableFilterUtil.compareFilters(cryptoCalendarFilters.get(EduCalendarFilterType.Impact), impact) : impact);
        Platform.dispatch(EduTradingSetLoading({value: false, page: RouteType.CryptocurrencyCalendar}));

        Platform.dispatch(EduTradingSetCryptoCalendarEvents({
            cryptoEvents: response
        }));

        Platform.dispatch(EduTradingSetCryptoCalendarFilters({
            filters: Filters
        }));
    }

    public DoGetMarketVideos = async ({videoType}: EduTradingFetchMarketVideosPayload): Promise<void> => {
        const page: RouteType = videoType === EduTradingMarketVideoType.MarketHighlight ? RouteType.MarketHighlightsTV : RouteType.MarketResearchVideos;
        const request = {
            category: videoType,
            limit: 100
        }
        Platform.dispatch(EduTradingSetLoading({value: true, page}));
        const path: string = "market-research-videos/get-recent-videos";
        let response: EduTradingMarketVideosResponse = await this.query(path, request, LanguageUtil.languageCode());

        if (Utils.isArrayEmpty(response)) {
            response = await this.query(path, request, LangCode.EN);
        }
        (response || []).forEach((video: EduTradingMarketVideo) => {
            video.file = FilesReplaceDomain(video.file);
            video.image = FilesReplaceDomain(video.image);
            video.teaser = FilesReplaceDomain(video.teaser);
        });
        Platform.dispatch(EduTradingSetMarketSearchVideos({videoType, videos: response}));
        Platform.dispatch(EduTradingSetLoading({value: false, page}));
    }

    public DoGetCurrencyStrengthMeter = async (): Promise<void> => {
        Platform.dispatch(EduTradingSetLoading({value: true, page: RouteType.CurrencyStrengthMeter}));

        const response: EduTradingCurrencyStrengthMeterResponse = await this.query("currency-strength-meter/get-strengths", {});

        Platform.dispatch(EduTradingSetLoading({value: false, page: RouteType.CurrencyStrengthMeter}));
        Platform.dispatch(EduTradingSetCurrencyStrengthMeter({currencies: response}))
    }

    public DoGetGlossary = async (): Promise<void> => {
        Platform.dispatch(EduTradingSetLoading({value: true, page: RouteType.Glossary}));

        const response: EduTradingGlossaryResponse = await this.query("glossary/get-terms");

        Platform.dispatch(EduTradingSetLoading({value: false, page: RouteType.Glossary}));
        Platform.dispatch(EduTradingSetGlossary({glossary: response}))
    }

    public DoGetTrendAnalysis = async (): Promise<void> => {
        Platform.dispatch(EduTradingSetLoading({value: true, page: RouteType.TrendAnalysis}));

        const response: EduTradingTrendAnalysisResponse = await this.query("trend-analyzer/get-trends");

        Platform.dispatch(EduTradingSetTrendAnalisys({trendAnalysis: response}));
        Platform.dispatch(EduTradingSetLoading({value: false, page: RouteType.TrendAnalysis}));
    }

    public DoGetEducationalPackages = async (): Promise<void> => {
        const answer: [HttpReject, GetPurchasePackagesResponse] = await Utils.to(XhrUtil.sendToTradingAcademyGet("GetAllPackages"));
        if (answer[1]) {
            const Packages: EduTradingPackage[] = [];
            Object.keys(answer[1]).forEach((id: string) => {
                const purchaseDetails: PackagePurchaseDetails = answer[1][id];
                Packages.push({
                    id: parseInt(id),
                    name: purchaseDetails.Name,
                    purchase: purchaseDetails,
                    applePackageId: purchaseDetails.ApplePackageId,
                })
            });
            Platform.dispatch(EduTradingSetPackages({
                Packages
            }));
            EduTradingState.instance().Packages = Packages;
        }
    }

    public DoGetEducationalContentTree = async (langCode: LangCode): Promise<void> => {
        Platform.dispatch(EduTradingSetLoadingTree({value: true}));
        const answer: [HttpReject, GetEducationalContentTree] = await Utils.to(XhrUtil.sendToTradingAcademyGet(`GetEducationalContentTree?languageCode=${langCode}`));
        Platform.dispatch(EduTradingSetLoadingTree({value: false}));

        if (answer[1]) {
            const {Courses} = answer[1];
            const DisplayCourses: EduTreeCourse[] = [];
            const MinPackageConfig: {[key: string]: string} = {};
            let EBooks: EduTreeLesson[] = [];
            let MarketAnalysis: EduTreeLesson[] = [];
            let MarketScanners: EduTreeLesson[] = [];
            let Tools: EduTreeLesson[] = [];
            Courses?.forEach((course: EduTreeCourse) => {
                if (course.Id === 8) {
                    course.Sections?.forEach((section: EduTreeSection) => {
                        if (section.Id === 12) {
                            EBooks = section.Lessons;
                        } else {
                            section.Lessons.forEach(
                                (lesson: EduTreeLesson) => MinPackageConfig[lesson.LessonGuid] = lesson.LeastRequiredPackageName
                            );
                            if (section.Id === 14) {
                                MarketAnalysis = section.Lessons;
                            } else if (section.Id === 15) {
                                MarketScanners = section.Lessons;
                            } else if (section.Id === 16) {
                                Tools = section.Lessons;
                            }
                        }
                    });
                } else {
                    DisplayCourses.push(course);
                }
            })

            const count: {[key: string]: number} = EduTradingPlaylistUtil.CalculateLessonsCount(DisplayCourses);

            Platform.dispatch(EduTradingSetAcademyPlaylistProgress({data: EduTradingPlaylistUtil.GetCoursesProgress(DisplayCourses)}));
            Platform.dispatch(EduTradingSetLessonsCount({
                all: count[EduTradingLessonsCountType.All],
                completed: count[EduTradingLessonsCountType.Completed]
            }));
            Platform.dispatch(EduTradingSetPackageTreeContent({
                Courses: DisplayCourses,
                EBooks,
                MarketAnalysis,
                MarketScanners,
                Tools,
                MinPackageConfig
            }));
            EduTradingState.instance().Courses = Courses;
        }
    }

    public DoGetActivePackage = async (): Promise<void> => {
        this._logger.debug("Do get active package");
        const answer: [HttpReject, GetActivePackageResponse] = await Utils.to(XhrUtil.sendToTradingAcademyGet("GetActivePackage"));
        if (answer[1]?.Id) {
            const {Id, Name, IsEnabled, PercentCompleted} = answer[1];
            this._logger.debug(`Got active package '${Name}'.`)
            Platform.dispatch(EduTradingSetActivePackage({
                Package: {
                    id: Id,
                    name: Name,
                    isEnabled: IsEnabled,
                    percentCompleted: Math.floor(PercentCompleted)
                }
            }));
            const {ActivePackage} = Platform.reduxState<StoreState>().eduTrading;
            if (Id && ActivePackage?.id && Id !== ActivePackage?.id) {
                await this.DoGetEducationalContentTree(LanguageUtil.languageCode());
            }
        }
    }

    public static DoGetLessonContentUrl = async (lesson: EduTreeLesson): Promise<string> => {
        const languageCode: LangCode = LanguageUtil.languageCode();
        Platform.dispatch(EduTradingSetLoadingTree({value: true}));
        const answer: [HttpReject, GetLessonContent] = await Utils.to(XhrUtil.sendToTradingAcademyGet(`GetLessonContent?lessonGuid=${lesson.LessonGuid}&languageCode=${languageCode}`));
        Platform.dispatch(EduTradingSetLoadingTree({value: false}));
        return answer[1]?.ContentUrl;
    }

    public DoCompleteLesson = async ({lessonGuid}: EduTradingCompleteLessonPayload): Promise<void> => {
        const request = {
            lessonGuid
        }

        const answer: [HttpReject, any] = await Utils.to(XhrUtil.sendToTradingAcademy(request, "CompleteLesson"));
        if (Utils.isNull(answer[0])) {
            this.DoGetEducationalContentTree(LanguageUtil.languageCode());
        }
    }
}
